import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import FlashInfo from './FlashInfo';
import { 
  Smartphone, 
  Wrench, 
  Calendar, 
  Euro, 
  CheckCircle, 
  Clock, 
  Package,
  AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RepairTracking = () => {
  const { repairNumber } = useParams();
  const [repair, setRepair] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [shopSettings, setShopSettings] = useState(null);

  useEffect(() => {
    fetchRepairStatus();
    fetchShopSettings();
  }, [repairNumber]);

  const fetchShopSettings = async () => {
    try {
      const response = await axios.get(`${API}/shop-settings`);
      setShopSettings(response.data);
    } catch (error) {
      console.error('Error fetching shop settings:', error);
    }
  };

  const fetchRepairStatus = async () => {
    try {
      const response = await axios.get(`${API}/track-public/${repairNumber}`);
      setRepair(response.data);
    } catch (error) {
      console.error('Error fetching repair status:', error);
      setError('Réparation non trouvée ou numéro invalide');
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      'en_cours': {
        label: 'En cours de réparation',
        icon: Clock,
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        description: 'Votre appareil est actuellement en cours de réparation par notre équipe technique.'
      },
      'termine': {
        label: 'Réparation terminée',
        icon: CheckCircle,
        color: 'bg-blue-100 text-blue-800 border-blue-200',
        description: 'La réparation de votre appareil est terminée. Vous pouvez venir le récupérer.'
      },
      'recupere': {
        label: 'Appareil récupéré',
        icon: Package,
        color: 'bg-green-100 text-green-800 border-green-200',
        description: 'Votre appareil a été récupéré. Merci de votre confiance !'
      }
    };
    
    return statusMap[status] || statusMap['en_cours'];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="text-center py-8">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Réparation non trouvée</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusInfo = getStatusInfo(repair.status);
  const StatusIcon = statusInfo.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="relative">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl flex items-center justify-center shadow-lg">
                <Smartphone className="w-8 h-8 text-white" />
                <Wrench className="w-6 h-6 text-white absolute -bottom-1 -right-1 bg-orange-500 rounded-full p-1" />
              </div>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Smart Service Mobile</h1>
          <p className="text-slate-600">Suivi de réparation - Liège</p>
        </div>

        {/* Flash Info */}
        <FlashInfo pageName="tracking" />

        {/* Repair Status */}
        <Card className="glass shadow-xl border-0">
          <CardHeader>
            <CardTitle className="flex items-center space-x-3">
              <StatusIcon className="w-6 h-6" />
              <span>Suivi de votre réparation</span>
            </CardTitle>
            <CardDescription>
              Numéro de réparation: <strong>{repair.repair_number}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Current Status */}
            <div className="text-center">
              <Badge className={`${statusInfo.color} border px-4 py-2 text-lg font-medium`}>
                <StatusIcon className="w-5 h-5 mr-2" />
                {statusInfo.label}
              </Badge>
              <p className="text-slate-600 mt-3">{statusInfo.description}</p>
            </div>

            {/* Repair Details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-slate-200">
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-slate-500">Client</p>
                  <p className="text-slate-900">{repair.client_name}</p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-slate-500">Appareil</p>
                  <p className="text-slate-900">{repair.device}</p>
                </div>

                {repair.repair_type && (
                  <div>
                    <p className="text-sm font-medium text-slate-500">Type de réparation</p>
                    <p className="text-slate-900">{repair.repair_type}</p>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-slate-500">Date d'entrée</p>
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <p className="text-slate-900">
                      {new Date(repair.entry_date).toLocaleDateString('fr-FR', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>

                {repair.estimated_exit_date && (
                  <div>
                    <p className="text-sm font-medium text-slate-500">Date prévue de fin</p>
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <p className="text-slate-900">
                        {new Date(repair.estimated_exit_date).toLocaleDateString('fr-FR', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                )}

                {repair.remaining_amount > 0 && (
                  <div>
                    <p className="text-sm font-medium text-slate-500">Reste à payer</p>
                    <div className="flex items-center space-x-2">
                      <Euro className="w-4 h-4 text-slate-400" />
                      <p className="text-xl font-bold text-slate-900">
                        {repair.remaining_amount.toFixed(2)} €
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Progress Timeline */}
            <div className="pt-4 border-t border-slate-200">
              <h3 className="font-medium text-slate-900 mb-4">Progression</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <div>
                    <p className="font-medium text-slate-900">Réparation reçue</p>
                    <p className="text-sm text-slate-600">
                      {new Date(repair.entry_date).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${
                    repair.status === 'en_cours' ? 'bg-yellow-500' :
                    repair.status === 'termine' || repair.status === 'recupere' ? 'bg-green-500' : 'bg-slate-300'
                  }`}></div>
                  <div>
                    <p className="font-medium text-slate-900">En cours de réparation</p>
                    <p className="text-sm text-slate-600">
                      {repair.status === 'en_cours' ? 'En cours...' : 'Terminé'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${
                    repair.status === 'termine' || repair.status === 'recupere' ? 'bg-green-500' : 'bg-slate-300'
                  }`}></div>
                  <div>
                    <p className="font-medium text-slate-900">Prêt à récupérer</p>
                    <p className="text-sm text-slate-600">
                      {repair.status === 'termine' ? 'Disponible' :
                       repair.status === 'recupere' ? 'Récupéré' : 'En attente'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card className="glass">
          <CardContent className="text-center py-6">
            <h3 className="font-semibold text-slate-900 mb-2">Questions ?</h3>
            <p className="text-slate-600 mb-4">
              Contactez-nous pour plus d'informations sur votre réparation
            </p>
            <div className="space-y-2 text-sm">
              <p><strong>Téléphone GSM:</strong> {shopSettings?.phone_mobile || '+32 498 45 42 40'}</p>
              {shopSettings?.phone_fixed && (
                <p><strong>Téléphone fixe:</strong> {shopSettings.phone_fixed}</p>
              )}
              <p><strong>Email:</strong> {shopSettings?.email || 'smartservicemobil@gmail.com'}</p>
              <p><strong>Adresse:</strong> {shopSettings?.address_street || 'Rue Saint Nicolas 325'}, {shopSettings?.address_zip || '4000'} {shopSettings?.address_city || 'Liège'}</p>
              {shopSettings?.website && (
                <p><strong>Site web:</strong> <a href={shopSettings.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{shopSettings.website}</a></p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RepairTracking;